import { removeFreshLead } from '../../../redux/freshLeadSlice';
import { useTierCountdown } from './useTierCountdown';
import { toast } from 'react-toastify';

import {
	VStack,
	HStack,
	Badge,
	Progress,
	Button,
	Box,
	Icon,
	Heading,
	Text,
	Tooltip,
	SimpleGrid,
	Alert,
	AlertIcon,
	AlertTitle,
	AlertDescription,
} from '@chakra-ui/react';
import {
	FiClock,
	FiAward,
	FiDollarSign,
	FiTarget,
	FiGlobe,
	FiX,
	FiZap,
} from 'react-icons/fi';
import { useDispatch } from 'react-redux';
import { useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaCoins, FaStar } from 'react-icons/fa';

const MotionBox = motion(Box);
const MotionVStack = motion(VStack);

const FreshLeadCard = ({
	lead,
	coins = 0,
	handleBuy,
	isDialerEnabled,
	isSubmitting,
	userLoading,
}) => {
	const dispatch = useDispatch();

	// const coins = user?.coins || 0;
	const leadValue = lead?.eLeadStatus === 'new' ? 300 : 50;

	const userIsEligible = Boolean(coins >= leadValue);
	const handleExpire = useCallback(() => {
		dispatch(removeFreshLead(lead._id));
		if (!isSubmitting) {
			toast.info('Lead offer expired! New leads coming soon...');
		}
	}, [dispatch, isSubmitting]);

	const { remaining, percentage } = useTierCountdown(
		lead?.expiresAt,
		handleExpire,
		lead?._id,
	);

	const handleSkip = useCallback(() => {
		dispatch(removeFreshLead(lead._id));
		toast.info('Lead skipped');
	}, [dispatch, lead?._id]);

	// Format long text with truncation
	const formatLongText = (text, maxLength = 25) => {
		if (!text) return 'N/A';
		return text.length > maxLength
			? `${text.substring(0, maxLength)}...`
			: text;
	};

	return (
		<MotionVStack
			spacing={3}
			align='stretch'
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.3 }}
			mb={4}
		>
			{/* Compact Progress Bar */}
			<Progress
				value={percentage}
				size='xs'
				w='full'
				borderRadius='full'
				bg='bg.elevated'
				sx={{
					'& > div': {
						transition: 'width 0.1s linear',
					},
				}}
				colorScheme={remaining <= 5 ? 'red' : 'green'}
			/>

			{/* Main Lead Card - Compact */}
			{lead?.isClaimed ? (
				<Box>
					<Alert
						status='error'
						variant='left-accent'
						borderRadius='md'
						fontSize='sm'
					>
						<AlertIcon />
						<Box>
							<AlertTitle>Lead Unavailable</AlertTitle>
							<AlertDescription>
								This lead has already been claimed and is no longer available.
							</AlertDescription>
						</Box>
					</Alert>
				</Box>
			) : (
				<>
					{/* Premium Badge - New Lead Alert */}
					<HStack spacing={2} justify='flex-end'>
						<Badge
							colorScheme='green'
							variant='subtle'
							fontSize='2xs'
							px={2}
							py={0.5}
							borderRadius='full'
							startElement={<Icon as={FiZap} size={12} />}
						>
							🔥 Fresh Lead • Available now
						</Badge>
					</HStack>

					<Box
						p={4}
						bg='bg.surface'
						borderRadius='xl'
						borderWidth='1px'
						borderColor='border.default'
						boxShadow='card'
						position='relative'
						overflow='hidden'
						_hover={{ borderColor: 'gold.primary', boxShadow: 'md' }}
						transition='all 0.2s'
						sx={{
							'&::-webkit-scrollbar': {
								display: 'none',
							},
							scrollbarWidth: 'none',
						}}
					>
						<VStack spacing={3} align='stretch'>
							{/* Header with Lead Name and Quick Actions */}
							<HStack justify='space-between' align='center'>
								<VStack align='start' spacing={0.5}>
									<Heading size='xs' noOfLines={1} color='text.heading'>
										{lead?.leadName || 'New Lead'}
									</Heading>
									<HStack spacing={1.5}>
										<Badge
											colorScheme='gold'
											fontSize='2xs'
											px={1.5}
											borderRadius='sm'
										>
											⚡ Premium
										</Badge>
									</HStack>
								</VStack>

								{/* Time Indicator */}
								<HStack
									spacing={1}
									bg='bg.elevated'
									px={2}
									py={1}
									borderRadius='full'
								>
									<Icon as={FiClock} size={12} color='text.muted' />
									<Text fontSize='2xs' fontWeight='medium' color='text.body'>
										{remaining > 0 ? `${remaining}s left` : 'Expired'}
									</Text>
								</HStack>
							</HStack>

							{/* Lead Details - Compact Grid */}
							<SimpleGrid columns={1} spacing={2}>
								<CompactInfoItem
									icon={FiDollarSign}
									label='Budget'
									value={formatLongText(lead?.budget, 25)}
									color='green.500'
								/>
								<CompactInfoItem
									icon={FiTarget}
									label='Campaign'
									value={formatLongText(lead?.leadCampaign, 25)}
									tooltip={lead?.leadCampaign}
									color='blue.500'
								/>
								<CompactInfoItem
									icon={FiGlobe}
									label='Ad Name'
									value={formatLongText(lead?.leadSourceDetails, 25)}
									tooltip={lead?.leadSourceDetails}
									color='purple.500'
								/>
								<CompactInfoItem
									icon={FaStar}
									label='Interest'
									value={formatLongText(lead?.interest, 25)}
									tooltip={lead?.interest}
									color='orange.500'
								/>
							</SimpleGrid>

							{/* Action Buttons - Compact */}
							<AnimatePresence>
								{!userIsEligible && (
									<Box
										flex={2}
										p={3}
										bg='rgba(212, 175, 55, 0.08)'
										border='1px solid'
										borderColor='border.gold'
										borderRadius='lg'
										fontSize='sm'
										color='text.accent'
									>
										You don’t have enough coins to buy this lead. Get more coins
										to unlock the latest premium leads.
									</Box>
								)}

								{remaining > 0 ? (
									<HStack spacing={2} w='full'>
										{userIsEligible && (
											<Button
												flex={2}
												h='36px'
												fontSize='sm'
												borderRadius='lg'
												leftIcon={<Icon as={FiAward} size={16} />}
												colorScheme='gold'
												isLoading={isSubmitting}
												isDisabled={isSubmitting || userLoading}
												onClick={() => handleBuy(lead._id)}
												size='sm'
											>
												{isDialerEnabled
													? `Buy & Call for ${leadValue}`
													: `Buy for ${leadValue}`}
											</Button>
										)}

										<Button
											flex={1}
											h='36px'
											fontSize='sm'
											borderRadius='lg'
											variant='outline'
											colorScheme='gray'
											isDisabled={isSubmitting}
											leftIcon={<Icon as={FiX} size={16} />}
											onClick={handleSkip}
											size='sm'
										>
											Skip
										</Button>
									</HStack>
								) : (
									<MotionBox
										initial={{ opacity: 0 }}
										animate={{ opacity: 1 }}
										w='full'
									>
										<Button
											w='full'
											h='36px'
											fontSize='sm'
											borderRadius='lg'
											isDisabled
											bg='bg.elevated'
											color='text.muted'
											leftIcon={<Icon as={FiClock} size={16} />}
											size='sm'
										>
											Lead Expired
										</Button>
									</MotionBox>
								)}
							</AnimatePresence>

							{/* Agent Tip - Small Hint */}
							{userIsEligible && remaining > 0 && remaining < 10 && (
								<Text fontSize='2xs' color='red.400' textAlign='center'>
									⚡ Hurry! Only {remaining} seconds left to grab this new lead
								</Text>
							)}
						</VStack>
					</Box>
				</>
			)}
		</MotionVStack>
	);
};

// Compact Info Item Component
const CompactInfoItem = ({ icon, label, value, tooltip, color }) => (
	<HStack spacing={1.5} align='center' bg='bg.elevated' p={1.5} borderRadius='md'>
		<Icon as={icon} boxSize={3} color={color} />
		<VStack spacing={0} align='start' flex={1}>
			<Text fontSize='xs' color='text.muted' lineHeight='1'>
				{label}
			</Text>
			<Tooltip label={tooltip} hasArrow placement='top' openDelay={300}>
				<Text fontSize='sm' fontWeight='medium' noOfLines={1} color='text.body'>
					{value || 'N/A'}
				</Text>
			</Tooltip>
		</VStack>
	</HStack>
);

export default FreshLeadCard;
